import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { getCookieByName } from '@99designs/browser';
import { getDesignInspirationByCategory } from '@99designs/feature-design-inspiration';
import {
    ProductLandingPage,
    getProductGroupPrices,
    getProductLandingGroups,
    productLandingGroupKeys,
} from '@99designs/feature-product-landing';
import { Site } from '@99designs/frontend-components/apps/global-nav/lib/Site';
import DefaultPageLayout from '../../components/common/DefaultPageLayout';
import { Head } from '../../components/common/Head/';
import { getDefaultPageProps } from '../../lib/getDefaultPageProps';
import type { ProductLandingGroupKey } from '@99designs/feature-product-landing';
import type { SiteConfig } from '@99designs/frontend-components/apps/global-nav/lib/Site';

export default function ProductLanding({
    siteConfig,
    categoryKey,
    randomFeaturedDesignerNumber,
    inspirationDesigns,
    userRole,
    productGroupPrices,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
    const site = new Site(JSON.parse(siteConfig) as SiteConfig);
    const productGroup = getProductLandingGroups(categoryKey as ProductLandingGroupKey);

    return (
        <DefaultPageLayout role={userRole} site={site}>
            <Head
                title={productGroup.title}
                description={productGroup.description}
                currentDomain={site.getDomain()}
                addSEOTags
            />
            <ProductLandingPage
                site={site}
                categoryKey={categoryKey}
                randomFeaturedDesignerNumber={randomFeaturedDesignerNumber}
                inspirationDesigns={inspirationDesigns}
                productGroupPrices={productGroupPrices}
                productGroup={productGroup}
            />
        </DefaultPageLayout>
    );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
    const urlSlug = context.query.urlSlug as ProductLandingGroupKey;
    const { req, res } = context;

    if (process.env.USE_CSRF_CHECK) {
        const csrfToken = getCookieByName('_99csrf', req.headers['cookie'] || '');
        if (!csrfToken) {
            res.statusCode = 401;
            res.end();
            return {
                props: {},
            };
        }
    }

    if (productLandingGroupKeys.includes(urlSlug)) {
        const [defaultPageProps, inspirationDesigns, productGroupPrices] = await Promise.all([
            getDefaultPageProps(context),
            getDesignInspirationByCategory(req, urlSlug),
            getProductGroupPrices(req, productLandingGroupKeys),
        ]);
        return {
            props: {
                ...defaultPageProps,
                categoryKey: urlSlug,
                randomFeaturedDesignerNumber: Math.random(),
                inspirationDesigns,
                productGroupPrices,
            },
        };
    }

    return {
        notFound: true,
    };
};
